import { Edit2, Plus, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCRM } from '../context/CRMContext';
import { Opportunity as Lead } from '../types'; // Use Opportunity type but alias as Lead for clarity in this component
import LeadFormSidebar from './LeadFormSidebar';

const Leads: React.FC = () => {
  const navigate = useNavigate();
  const { opportunities, addOpportunity, updateOpportunity, deleteOpportunity } = useCRM();
  
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>(opportunities);
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);

  useEffect(() => {
    setFilteredLeads(opportunities);
  }, [opportunities]);

  const handleSearch = (term: string) => {
    const lowercasedTerm = term.toLowerCase();
    const filtered = opportunities.filter(op =>
      op.companyName?.toLowerCase().includes(lowercasedTerm) ||
      op.contactName?.toLowerCase().includes(lowercasedTerm) ||
      op.salesRep.toLowerCase().includes(lowercasedTerm)
    );
    setFilteredLeads(filtered);
  };

  const handleSaveLead = (lead: Omit<Lead, 'id' | 'createdDate' | 'lastModifiedDate'>) => {
    if (editingLead) {
      updateOpportunity(editingLead.id, lead);
    } else {
      addOpportunity(lead);
    }
    setEditingLead(null);
    setIsFormOpen(false);
  };

  const handleEdit = (lead: Lead) => {
    setEditingLead(lead);
    setIsFormOpen(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
        deleteOpportunity(id);
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedLeads(filteredLeads.map(op => op.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectOne = (id: string) => {
    if (selectedLeads.includes(id)) {
      setSelectedLeads(selectedLeads.filter(opId => opId !== id));
    } else {
      setSelectedLeads([...selectedLeads, id]);
    }
  };

  return (
    <div className="h-full bg-gray-50">
      <div className="flex flex-col overflow-hidden h-full">
        <div className="flex-1 overflow-y-auto p-6">
          <div className="bg-white shadow-sm rounded-lg h-full flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl font-semibold text-gray-900">Leads</h1>
                <button 
                  onClick={() => { setEditingLead(null); setIsFormOpen(true); }}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  <Plus size={16} />
                  <span>New Lead</span>
                </button>
              </div>
              <div className="flex items-center space-x-4">
                <input
                  type="text"
                  placeholder="Search by name, company, or rep..."
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="overflow-x-auto flex-1">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedLeads.length === filteredLeads.length && filteredLeads.length > 0}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                      />
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sales Rep</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company / Contact</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deal Stage</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company Size</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expected Deal Size</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredLeads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/leads/${lead.id}`)}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedLeads.includes(lead.id)}
                          onChange={() => handleSelectOne(lead.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{lead.salesRep}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{lead.companyName || 'N/A'}</div>
                        {lead.contactName && (
                          <div className="text-xs text-gray-500">
                            Contact: {lead.contactName}
                            {lead.contactEmail && (
                              <div className="text-xs text-blue-600 mt-1">{lead.contactEmail}</div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{lead.dealStage}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{lead.companySize}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{lead.expectedDealSize}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button onClick={(e) => { e.stopPropagation(); handleEdit(lead); }} className="text-gray-600 hover:text-blue-600">
                            <Edit2 size={16} />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleDelete(lead.id); }} className="text-gray-600 hover:text-red-600">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <LeadFormSidebar
        isOpen={isFormOpen}
        lead={editingLead}
        onSave={handleSaveLead}
        onClose={() => {
          setIsFormOpen(false);
          setEditingLead(null);
        }}
      />
    </div>
  );
};

export default Leads;
